"use client";

import { useRef } from "react";
import clsx from "clsx";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const TONES = {
  light: "bg-[var(--blanco)] text-[var(--fondoGeneral-secciones)]",
  muted: "bg-[var(--fondo-auxiliar2)] text-[var(--fondoGeneral-secciones)]",
  dark: "bg-[var(--fondoGeneral-secciones)] text-[var(--blanco)]",
} as const;

type SectionShellProps = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  tone?: keyof typeof TONES;
  /** Controles alineados con el encabezado (pausa, filtros, etc.) */
  headerAside?: React.ReactNode;
  /** Contenido de borde a borde: se renderiza fuera del contenedor */
  bleed?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
};

/**
 * Contenedor y encabezado compartidos por las secciones del home.
 * Fija el ancho, el ritmo vertical y la jerarquía del título para que las
 * secciones no vuelvan a definir cada una sus propias medidas.
 */
export default function SectionShell({
  eyebrow,
  title,
  subtitle,
  tone = "light",
  headerAside,
  bleed,
  children,
  className,
}: SectionShellProps) {
  const isDark = tone === "dark";
  const headerRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const tl = gsap.timeline({
          scrollTrigger: { trigger: headerRef.current, start: "top 88%", once: true },
        });
        // El filete se dibuja y el texto entra detrás: el orden importa,
        // el filete es lo que ancla la mirada al inicio de la sección.
        // `fromTo` con destino explícito para que un remontaje no deje el
        // encabezado congelado en su estado inicial.
        tl.fromTo(
          ".js-eyebrow-rule",
          { scaleX: 0 },
          {
            scaleX: 1,
            transformOrigin: "left center",
            duration: 0.5,
            ease: "power2.out",
          },
        ).fromTo(
          ".js-header-text",
          { y: 16, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.55,
            ease: "power2.out",
            stagger: 0.08,
          },
          "-=0.3",
        );
      });

      return () => mm.revert();
    },
    { scope: headerRef },
  );

  return (
    <section className={clsx("w-full py-16 md:py-24", TONES[tone], className)}>
      <div className="w-[92%] max-w-7xl mx-auto">
        <header
          ref={headerRef}
          className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-10 md:mb-14"
        >
          <div className="flex flex-col gap-3 max-w-3xl">
            {eyebrow && (
              <span
                className={clsx(
                  "js-header-text inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold",
                  isDark
                    ? "text-[var(--blanco)]/70"
                    : "text-[var(--subtitulos-descripciones-iconos)]",
                )}
              >
                <span className="js-eyebrow-rule w-8 h-px bg-[var(--botonesSecundarios-iconoLlamativo)]" />
                {eyebrow}
              </span>
            )}
            <h2 className="js-header-text text-3xl md:text-4xl font-bold tracking-tight text-balance">
              {title}
            </h2>
            {subtitle && (
              <p
                className={clsx(
                  "js-header-text text-base leading-relaxed",
                  isDark
                    ? "text-[var(--blanco)]/75"
                    : "text-[var(--subtitulos-descripciones-iconos)]",
                )}
              >
                {subtitle}
              </p>
            )}
          </div>
          {headerAside && <div className="shrink-0">{headerAside}</div>}
        </header>
      </div>

      {bleed}

      {children && <div className="w-[92%] max-w-7xl mx-auto">{children}</div>}
    </section>
  );
}
